import { groupLabelCode } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import { PlusIcon, PrinterIcon, TagIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/empty-state";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/misc";
import { useFormat } from "@/lib/format";
import { useTRPC } from "@/lib/trpc";
import { AddBlankDialog, type LabelItem, LabelRow, useShowError } from "./shared";

/** Labels › Blank: codes that belong to no part yet; reprinted or discarded. */
export function BlankTab({ labels }: { labels: LabelItem[] | undefined }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const showError = useShowError();
  const [adding, setAdding] = useState(false);
  const [discarding, setDiscarding] = useState<string | null>(null);
  const reprint = useMutation({
    ...trpc.labels.reprint.mutationOptions(),
    onSuccess: () => toast.success(t("labels.inBasket")),
    onError: showError,
  });
  const retire = useMutation({ ...trpc.labels.retire.mutationOptions(), onError: showError });
  const add = (
    <Button onClick={() => setAdding(true)}>
      <PlusIcon />
      {t("labels.addBlank")}
    </Button>
  );
  return (
    <div className="flex flex-col gap-4">
      {labels === undefined ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : labels.length === 0 ? (
        <EmptyState
          icon={TagIcon}
          title={t("labels.blankEmpty")}
          body={t("labels.blankEmptyBody")}
          action={add}
        />
      ) : (
        <>
          <div className="flex justify-end">{add}</div>
          <Card className="divide-y divide-border">
            {labels.map((label) => (
              <LabelRow
                key={label.code}
                label={label}
                actions={
                  <>
                    <span className="hidden text-xs text-muted-foreground sm:inline">
                      {label.lastPrintedAt
                        ? t("labels.printedOn", { date: format.date(label.lastPrintedAt) })
                        : t("labels.notPrinted")}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("labels.reprintCode", { code: groupLabelCode(label.code) })}
                      disabled={reprint.isPending}
                      onClick={() => reprint.mutate({ code: label.code })}
                    >
                      <PrinterIcon />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("labels.discardCode", { code: groupLabelCode(label.code) })}
                      onClick={() => setDiscarding(label.code)}
                    >
                      <Trash2Icon />
                    </Button>
                  </>
                }
              />
            ))}
          </Card>
        </>
      )}
      <AddBlankDialog open={adding} onOpenChange={setAdding} />
      <ConfirmDialog
        open={discarding !== null}
        onOpenChange={(open) => !open && setDiscarding(null)}
        title={t("labels.discardTitle", { code: discarding ? groupLabelCode(discarding) : "" })}
        description={t("labels.discardBody")}
        confirmLabel={t("labels.discard")}
        destructive
        pending={retire.isPending}
        onConfirm={() =>
          discarding &&
          retire.mutate({ code: discarding }, { onSuccess: () => setDiscarding(null) })
        }
      />
    </div>
  );
}
